import React, { useEffect, useState } from 'react'
import {
  Download,
  EyeOff,
  HardDrive,
  KeyRound,
  Layers,
  Lock,
  Mail,
  Moon,
  Paperclip,
  RotateCcw,
  Search,
  ShieldCheck,
} from 'lucide-react'

// Set this once the extension is live on the Chrome Web Store.
const CHROME_STORE_URL = ''
const CONTACT_EMAIL = 'contact@forgen.ai'
const IMG = '/imgs/ghostkeep'

const features = [
  {
    icon: Download,
    title: 'Saves automatically',
    text: 'Temporary chats are saved as you chat. No buttons to remember.',
  },
  {
    icon: Paperclip,
    title: 'Keeps your files too',
    text: 'Documents you upload and files ChatGPT creates for you (Word, Excel, PDF, images and more), with the right file names.',
  },
  {
    icon: Layers,
    title: 'Save every open chat',
    text: 'One click saves every open temporary chat at once, in the background.',
  },
  {
    icon: Search,
    title: 'Read, search, export',
    text: 'Search your saved chats, copy them, export them as Markdown and download their files.',
  },
  {
    icon: RotateCcw,
    title: 'Encrypted backup',
    text: 'A backup in your Downloads folder lets you restore everything after reinstalling.',
  },
  {
    icon: Moon,
    title: 'Light and dark mode',
    text: 'Looks right whichever theme you use.',
  },
]

const privacyPoints = [
  {
    icon: HardDrive,
    title: 'Nothing leaves your computer',
    text: 'No accounts, no servers, no analytics, no tracking.',
  },
  {
    icon: Lock,
    title: 'Everything is encrypted',
    text: 'Every chat and file is encrypted with AES-256-GCM, using an RSA-3072 key pair protected by your passphrase.',
  },
  {
    icon: KeyRound,
    title: 'Only you can read it',
    text: 'Saving works without your passphrase. Reading always needs it. Your passphrase is never stored.',
  },
  {
    icon: EyeOff,
    title: 'Unlock once per session',
    text: 'Optionally stay unlocked until Chrome closes, so you only type your passphrase once.',
  },
]

const screenshots = [
  { src: `${IMG}/1-saved-chats.png`, caption: 'Read your saved chats and download their files.' },
  { src: `${IMG}/3-search-dark.png`, caption: 'Search every saved chat, in light or dark mode.' },
  { src: `${IMG}/4-setup-restore.png`, caption: 'Choose a passphrase, or restore from your backup.' },
]

const steps = [
  'Install Ghostkeep and choose a passphrase.',
  'Start a temporary chat on chatgpt.com. Ghostkeep saves it as you go.',
  'Click the Ghostkeep icon, then "Open saved chats" to read your chats, search them and download their files.',
]

const brandGradient = 'bg-gradient-to-br from-[#7c4dff] via-[#4f6ef0] to-[#14b2a3]'

const InstallButton = ({ light = false }) =>
  CHROME_STORE_URL ? (
    <a
      href={CHROME_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-transform hover:scale-105 ${
        light ? 'bg-white text-[#5b34d6]' : 'bg-[#6d3fe0] text-white'
      }`}
    >
      <Download className="w-5 h-5" />
      Add to Chrome, it's free
    </a>
  ) : (
    <span
      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold ${
        light ? 'bg-white/20 text-white' : 'bg-[#6d3fe0]/10 text-[#5b34d6]'
      }`}
    >
      Coming soon to the Chrome Web Store
    </span>
  )

const Ghostkeep = () => {
  const [enlarged, setEnlarged] = useState(null)

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Ghostkeep: temporary chats, kept safe'
    return () => {
      document.title = previousTitle
    }
  }, [])

  useEffect(() => {
    if (!enlarged) return
    const onKey = (e) => e.key === 'Escape' && setEnlarged(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enlarged])

  return (
    <div className="min-h-screen bg-[#f7f6fb] text-[#1c1a24]">
      {/* Hero */}
      <section className={`${brandGradient} text-white`}>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src={`${IMG}/icon128.png`}
              alt="Ghostkeep icon"
              className="w-20 h-20 rounded-2xl shadow-xl mb-8"
            />
            <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-4">
              Temporary chats,
              <br />
              kept safe.
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-lg">
              ChatGPT's temporary chats vanish like ghosts. Ghostkeep quietly saves them, and
              their files, encrypted on your own computer, so closing Chrome doesn't erase them.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <InstallButton light />
              <a href="#privacy" className="text-white/90 font-semibold hover:underline">
                How your data stays private
              </a>
            </div>
          </div>
          <img
            src={`${IMG}/2-popup.png`}
            alt="The Ghostkeep popup saving a temporary chat"
            className="w-full rounded-2xl shadow-2xl ring-1 ring-white/20"
          />
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">What it does</h2>
        <p className="text-lg text-[#6c6880] text-center max-w-2xl mx-auto mb-12">
          Temporary chats are great for privacy, until you lose a conversation you needed.
          Ghostkeep keeps them for you.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, text }) => (
            <div key={title} className="bg-white border border-[#e5e2ee] rounded-2xl p-6">
              <div className="w-11 h-11 rounded-xl bg-[#6d3fe0]/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#6d3fe0]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{title}</h3>
              <p className="text-[#6c6880]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Screenshots */}
      <section className="bg-white border-y border-[#e5e2ee]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">See it in action</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {screenshots.map(({ src, caption }) => (
              <figure key={src}>
                <button
                  type="button"
                  onClick={() => setEnlarged(src)}
                  className="block w-full rounded-xl overflow-hidden shadow-lg ring-1 ring-[#e5e2ee] transition-transform hover:scale-[1.02] cursor-zoom-in"
                >
                  <img src={src} alt={caption} className="w-full" />
                </button>
                <figcaption className="text-sm text-[#6c6880] text-center mt-3">{caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section id="privacy" className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-center justify-center gap-3 mb-4">
          <ShieldCheck className="w-8 h-8 text-[#14b2a3]" />
          <h2 className="text-3xl md:text-4xl font-bold">Private by design</h2>
        </div>
        <p className="text-lg text-[#6c6880] text-center max-w-2xl mx-auto mb-12">
          Your chats are stored only on your computer, encrypted with a passphrase only you know.
          We never receive, see, or have access to any of your data.
        </p>
        <div className="grid sm:grid-cols-2 gap-6">
          {privacyPoints.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex gap-4 bg-white border border-[#e5e2ee] rounded-2xl p-6">
              <Icon className="w-6 h-6 text-[#14b2a3] shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold mb-1">{title}</h3>
                <p className="text-[#6c6880]">{text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center mt-8">
          <a href="/ghostkeep/privacy-policy.html" className="text-[#6d3fe0] font-semibold hover:underline">
            Read the full privacy policy
          </a>
        </p>
      </section>

      {/* How to use */}
      <section className="bg-white border-y border-[#e5e2ee]">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">How to use it</h2>
          <ol className="space-y-6">
            {steps.map((step, i) => (
              <li key={step} className="flex gap-4 items-start">
                <span className={`${brandGradient} text-white font-bold w-9 h-9 rounded-full flex items-center justify-center shrink-0`}>
                  {i + 1}
                </span>
                <p className="text-lg pt-1">{step}</p>
              </li>
            ))}
          </ol>

          <div className="mt-14 border-l-4 border-[#6d3fe0] pl-5 space-y-2 text-[#6c6880]">
            <h3 className="font-semibold text-[#1c1a24]">Good to know</h3>
            <p>If you forget your passphrase, your saved chats can't be recovered by anyone.</p>
            <p>Files you export or download from Ghostkeep are regular, unencrypted files.</p>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className={`${brandGradient} text-white text-center`}>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Stop losing temporary chats.</h2>
          <InstallButton light />
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-[#6c6880] flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <div className="space-y-1">
          <p>
            Copyright © 2026{' '}
            <a href="/" className="hover:underline">
              ForGen AI, LLC
            </a>
            . All rights reserved.
          </p>
          <p>
            Ghostkeep is an independent project. It is not made by or affiliated with OpenAI.
            ChatGPT is a trademark of OpenAI.
          </p>
        </div>
        <div className="flex gap-6 shrink-0">
          <a href="/ghostkeep/privacy-policy.html" className="hover:underline">
            Privacy policy
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-1 hover:underline">
            <Mail className="w-4 h-4" />
            {CONTACT_EMAIL}
          </a>
        </div>
      </footer>

      {/* Screenshot lightbox */}
      {enlarged && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setEnlarged(null)}
        >
          <img src={enlarged} alt="" className="max-w-full max-h-full rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  )
}

export default Ghostkeep
